// Caption track download URLs and parsers (json3 preferred; srv1/srv3 XML as fallback). No DOM needed.

import type { TranscriptSegment } from '../api';

/** Sets fmt=json3 on a caption track baseUrl (native clients return fmt=srv3 by default). */
export function timedtextUrl(baseUrl: string, fmt: 'json3' | 'srv3' | 'srv1' = 'json3'): string {
  const url = new URL(baseUrl, 'https://www.youtube.com');
  url.searchParams.set('fmt', fmt);
  return url.toString();
}

/** True if the URL is gated behind a Proof-of-Origin token (WEB-client tracks, "exp=xpe"). */
export function needsPoToken(baseUrl: string): boolean {
  return /[?&]exp=xpe\b/.test(baseUrl);
}

/** Parses a timedtext body in json3, srv3 or srv1 format. Returns [] for empty bodies. */
export function parseTimedText(body: string): TranscriptSegment[] {
  const trimmed = body.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('{')) return parseJson3(trimmed);
  if (trimmed.startsWith('<')) return parseXml(trimmed);
  return [];
}

interface Json3 {
  events?: { tStartMs?: number; dDurationMs?: number; aAppend?: number; segs?: { utf8?: string }[] }[];
}

function parseJson3(body: string): TranscriptSegment[] {
  let data: Json3;
  try {
    data = JSON.parse(body) as Json3;
  } catch {
    return [];
  }
  const segments: TranscriptSegment[] = [];
  for (const event of data.events ?? []) {
    if (!event.segs || event.aAppend) continue; // window definitions and "\n" line appends (ASR)
    const text = clean(event.segs.map((s) => s.utf8 ?? '').join(''));
    if (text) segments.push({ start: ms(event.tStartMs), text });
  }
  return segments;
}

function parseXml(body: string): TranscriptSegment[] {
  const segments: TranscriptSegment[] = [];
  // srv3: <p t="1520" d="1880">text or <s>word</s>...</p>
  for (const m of body.matchAll(/<p\b([^>]*)>([\s\S]*?)<\/p>/g)) {
    const t = /\bt="(\d+)"/.exec(m[1] ?? '')?.[1];
    const text = clean(decodeEntities(stripTags(m[2] ?? '')));
    if (t !== undefined && text) segments.push({ start: ms(Number(t)), text });
  }
  if (segments.length) return segments;
  // srv1: <text start="1.36" dur="1.68">text</text> (entities are double-escaped)
  for (const m of body.matchAll(/<text\b([^>]*)>([\s\S]*?)<\/text>/g)) {
    const start = /\bstart="([\d.]+)"/.exec(m[1] ?? '')?.[1];
    const text = clean(decodeEntities(decodeEntities(stripTags(m[2] ?? ''))));
    if (start !== undefined && text) segments.push({ start: round(Number(start)), text });
  }
  return segments;
}

const ms = (value: number | undefined) => round((value ?? 0) / 1000);
const round = (seconds: number) => Math.round(seconds * 1000) / 1000;
const clean = (text: string) => text.replace(/\s+/g, ' ').trim();
const stripTags = (text: string) => text.replace(/<[^>]*>/g, '');

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, name: string) => {
    if (name[0] === '#') {
      const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : entity;
    }
    return NAMED[name.toLowerCase()] ?? entity;
  });
}
